import { SUBMITTED } from "@equidefi/constants/accreditation";
import type { DTO } from "@equidefi/shared/types";
import { ApiClient } from "./ApiClient";

class AccreditationClient extends ApiClient {
  async getDocuments(offeringId: string, status = SUBMITTED) {
    if (!offeringId) return null;

    const response = await this.get(`/v2/accreditation/documents/${offeringId}?status=${status}`);
    return response.data;
  }

  async getPresignedUrl(investmentId: string, filename: string, fileExtension: string) {
    const response = await this.post(`/v2/accreditation/document/${investmentId}`, {
      filename,
      fileExtension,
    });
    return response.data;
  }

  async submitVerificationReport(investmentId: string, filename: string, extension: string) {
    const response = await this.post(`/v2/accreditation/verification/${investmentId}`, {
      filename,
      extension,
    });
    return response.data;
  }

  async getReviewToken(offeringId: string) {
    const response = await this.post("/v2/accreditation/review-token", {
      offering_id: offeringId,
    });
    return response.data;
  }

  async uploadToken(offeringId: string) {
    const response = await this.post("/v2/accreditation/upload-token", {
      offering_id: offeringId,
    });
    return response.data;
  }

  async getVaultRecord(id: string) {
    const response = await this.get(`/v2/accreditation/vault-record/${id}`);
    return response.data;
  }

  async getDownloadUrls(vaultId?: string) {
    const response = await this.get(`/v2/accreditation/download-urls/${vaultId}`);
    return response.data;
  }

  async getAccreditationFiles(vaultId: string) {
    // Guard against an explicit refetch firing before the vault id is available,
    // which would request `/files/undefined` and 500 on the backend.
    if (!vaultId || vaultId === "undefined") return [];

    const response = await this.get<DTO.AccreditationFile[]>(`/v2/accreditation/files/${vaultId}`);
    return response.data;
  }

  async storeAccreditationFile(data: {
    investmentId: string;
    name: string;
    filetype: string;
    type: string;
    url: string;
  }) {
    const { investmentId, ...rest } = data;
    const response = await this.post<DTO.AccreditationFile>(`/v2/accreditation/files/${investmentId}`, rest);
    return response.data;
  }

  async updateAccreditationFile(vaultId: string, fileId: string, data: { name?: string; type?: string }) {
    const response = await this.put(`/v2/accreditation/files/${vaultId}/${fileId}`, data);
    return response.data;
  }

  async removeAccreditationFile(vaultId: string, fileId: string) {
    const response = await this.delete(`/v2/accreditation/files/${vaultId}/${fileId}`);
    return response.data;
  }

  async updateVault(investmentId: string, data: object) {
    const response = await this.post(`/v2/accreditation/vault/${investmentId}`, data);
    return response.data;
  }

  async accept(
    accreditationId: string,
    data: {
      file?: string;
      additional_info?: boolean;
      accreditation_method?: string;
    },
  ) {
    const response = await this.post(`/v2/accreditation/accept/${accreditationId}`, data);
    return response.data;
  }

  async reject(accreditationId: string, file: string, reason: string) {
    const response = await this.post(`/v2/accreditation/reject/${accreditationId}`, { file, reason });

    return response.data;
  }

  async getIsApproved(investmentId: string) {
    const response = await this.get(`/v2/accreditation/approved/${investmentId}`);

    return response.data;
  }

  async submitPreApproved(investmentId: string) {
    const response = await this.post(`/v2/accreditation/preapproved/${investmentId}`);

    return response.data;
  }

  async manuallySubmit(vaultId: string) {
    const response = await this.post(`/v2/accreditation/manually-submit/${vaultId}`);

    return response.data;
  }
}

export default AccreditationClient;
